"use client";

import { CardData } from "@/components/wboard/nodes-edges";
import { Edge, Node, ReactFlowInstance } from "@xyflow/react";
import {
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

interface VotingData {
  name: string;
  vote: number;
  token_id: string;
}

interface TokenData {
  url: string;
  name: string;
  description: string;
}

interface IsVotes {
  voted: boolean;
  name: string;
}

interface ParentDetails {
  name: string;
  node_id: number;
  dna_list: string[];
  description: string;
}

// interface TimeType {
//   minutes: number;
//   seconds: number;
//   milliseconds: number;
// }

interface CatDevContextType {
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setReactFlowInstance: (
    instance: ReactFlowInstance<Node, Edge> | null
  ) => void;
  centerNode: (nodeId: string) => void;
  reactFlowInstance: ReactFlowInstance<Node, Edge> | null;
  expandedNodes: string[];
  setExpandedNodes: React.Dispatch<React.SetStateAction<string[]>>;
  realWinner: CardData[];
  setRealWinner: React.Dispatch<React.SetStateAction<CardData[]>>;
  votingData: VotingData[];
  tokenData: TokenData[];
  isVotes: IsVotes;
  setIsVotes: React.Dispatch<React.SetStateAction<IsVotes>>;
  setVotingData: React.Dispatch<React.SetStateAction<VotingData[]>>;
  setTokenData: React.Dispatch<React.SetStateAction<TokenData[]>>;
  parentDetails: ParentDetails | null;
  setParentDetails: React.Dispatch<React.SetStateAction<ParentDetails | null>>;
}

const CatDevContext = createContext<CatDevContextType | null>(null);

export const CatDevProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<string[]>(["1"]);
  const [realWinner, setRealWinner] = useState<CardData[]>([]);
  const [votingData, setVotingData] = useState<VotingData[]>([]);
  const [tokenData, setTokenData] = useState<TokenData[]>([]);
  const [isVotes, setIsVotes] = useState<IsVotes>({ voted: false, name: "" });
  const [parentDetails, setParentDetails] = useState<ParentDetails | null>(
    null
  );

  // useEffect(() => {
  //   let interval: NodeJS.Timeout;

  //   const fetchEndTime = async () => {
  //     try {
  //       setIsLoading(true);
  //       const response = await fetch(
  //         "https://api.catdevai.com/api/current-set-time/"
  //       );
  //       const data = await response.json();
  //       const createdAt = new Date(data.created_at);
  //       const endTime = new Date(createdAt.getTime() + 60 * 60 * 1000); // +1 hour
  //       const now = new Date();
  //       if (endTime <= now) {
  //         setTime({ minutes: 0, seconds: 0, milliseconds: 0 });
  //         return null;
  //       }
  //       return endTime;
  //     } catch (error) {
  //       // console.error("Error fetching end time:", error);
  //       toast.error(
  //         `Some thing went wrong: ${error instanceof Error ? error.message : error}`
  //       );
  //       return null;
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   const updateTimer = (endTime: Date) => {
  //     const now = new Date();
  //     const diff = endTime.getTime() - now.getTime();
  //     if (diff <= 0) {
  //       setTime({ minutes: 0, seconds: 0, milliseconds: 0 });
  //       return false;
  //     }
  //     setTime({
  //       minutes: Math.floor(diff / (1000 * 60)),
  //       seconds: Math.floor((diff % (1000 * 60)) / 1000),
  //       milliseconds: Math.floor((diff % 1000) / 10),
  //     });
  //     return true;
  //   };

  //   const initializeCountdown = async () => {
  //     const endTime = await fetchEndTime();
  //     if (endTime) {
  //       interval = setInterval(() => {
  //         if (!updateTimer(endTime)) {
  //           clearInterval(interval);
  //         }
  //       }, 10);
  //     }
  //   };

  //   initializeCountdown();

  //   return () => clearInterval(interval);
  // }, []);

  // This function is exposed to consumers of the context.

  const centerNode = useCallback(
    (nodeId: string) => {
      if (reactFlowInstance) {
        const node = reactFlowInstance.getNode(nodeId);
        if (!node) return;
        console.log("Centering on node:", node);

        reactFlowInstance.fitView({
          nodes: [node],
          minZoom: 0.5,
          maxZoom: 1.5,
          padding: 0.2, // Extra space around the node
          duration: 800, // Animation duration in ms
        });
        console.log("Centering on node after in context:");
      }
    },
    [reactFlowInstance]
  );

  return (
    <CatDevContext.Provider
      value={{
        isLoading,
        setIsLoading,
        setReactFlowInstance,
        centerNode,
        reactFlowInstance,
        expandedNodes,
        setExpandedNodes,
        realWinner,
        setRealWinner,
        votingData,
        tokenData,
        isVotes,
        setIsVotes,
        setVotingData,
        setTokenData,
        parentDetails,
        setParentDetails,
      }}
    >
      {children}
    </CatDevContext.Provider>
  );
};

export const useCatDev = () => {
  const context = useContext(CatDevContext);
  if (!context) {
    throw new Error("useCatDev must be used within a CatDevProvider");
  }
  return context;
};
